import React from "react";
import { getFeed, likeQuestion, adminDeleteQuestion, adminClearFeed, reportQuestion, adminPinQuestion, adminHideQuestion } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AVATAR_HERO } from "../lib/constants";
import { Heart, MessageCircleQuestion, Loader2, Trash2, Flag, Pin, Star, EyeOff, RotateCcw } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { toast } from "sonner";

const REPORT_REASONS = [
  { id: "spam", label: "Spam" },
  { id: "abusive", label: "Abusive / harmful" },
  { id: "off-topic", label: "Off-topic" },
  { id: "other", label: "Other" },
];

export const WisdomWall = React.forwardRef((props, ref) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [liked, setLiked] = React.useState({});
  const [reported, setReported] = React.useState({});
  const [clearing, setClearing] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const data = await getFeed();
      setItems(data);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  React.useImperativeHandle(ref, () => ({ refresh: load }), [load]);

  const onLike = async (id) => {
    if (liked[id]) return;
    setLiked((p) => ({ ...p, [id]: true }));
    setItems((prev) =>
      prev.map((it) => (it.id === id ? { ...it, likes: it.likes + 1 } : it))
    );
    try {
      await likeQuestion(id);
    } catch (e) {
      /* ignore */
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this post from the Wisdom Wall?")) return;
    const prev = items;
    setItems((p) => p.filter((it) => it.id !== id));
    try {
      await adminDeleteQuestion(id);
      toast.success("Post removed.");
    } catch (e) {
      setItems(prev);
      toast.error("Could not delete. Are you logged in as admin?");
    }
  };

  const onClearAll = async () => {
    if (!window.confirm("This will permanently remove ALL posts on the Wisdom Wall. Continue?")) return;
    setClearing(true);
    try {
      const res = await adminClearFeed();
      setItems([]);
      toast.success(`Wisdom Wall cleared (${res.deleted} removed).`);
    } catch (e) {
      toast.error("Could not clear. Are you logged in as admin?");
    } finally {
      setClearing(false);
    }
  };

  const onReport = async (id, reason) => {
    if (reported[id]) return;
    setReported((p) => ({ ...p, [id]: true }));
    try {
      const res = await reportQuestion(id, reason);
      toast.success(res.auto_hidden ? "Reported — post hidden pending review." : "Thanks — we'll review this post.");
    } catch (e) {
      toast.error("Could not report. Please try again.");
    }
  };

  const onToggleHide = async (id, currentlyHidden) => {
    try {
      await adminHideQuestion(id, !currentlyHidden);
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, hidden: !currentlyHidden } : it)));
      toast.success(!currentlyHidden ? "Post hidden from public." : "Post restored to the Wall.");
    } catch (e) {
      toast.error("Could not update. Are you logged in as admin?");
    }
  };

  const onTogglePin = async (id, currentlyPinned) => {
    try {
      await adminPinQuestion(id, !currentlyPinned);
      setItems((prev) => {
        const next = prev.map((it) => (it.id === id ? { ...it, pinned: !currentlyPinned } : it));
        return [...next].sort((a, b) => (b.pinned === true) - (a.pinned === true));
      });
      toast.success(!currentlyPinned ? "Pinned to the top." : "Unpinned.");
    } catch (e) {
      toast.error("Could not update. Are you logged in as admin?");
    }
  };

  return (
    <section id="wall" className="relative scroll-mt-20 bg-sand-dark/40 border-y border-border">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-24">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div className="max-w-2xl">
            <span className="text-sm uppercase tracking-[0.2em] font-bold text-clay">
              The Wisdom Wall
            </span>
            <h2 className="font-display font-black text-ink text-3xl sm:text-4xl lg:text-5xl mt-3 leading-tight">
              Real questions. Real Shabba Robo answers.
            </h2>
          </div>
          <p className="text-ink/60 text-sm">
            Fresh wisdom from people across Naija &amp; Africa.
          </p>
        </div>

        {isAdmin && items.length > 0 && (
          <div className="mt-6">
            <button
              onClick={onClearAll}
              disabled={clearing}
              className="inline-flex items-center gap-2 rounded-full bg-ink text-sand-light text-sm font-bold px-5 py-2.5 hover:opacity-90 disabled:opacity-60"
              data-testid="wall-clear-all-btn"
            >
              {clearing ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
              Clear entire Wisdom Wall
            </button>
            <p className="text-xs text-ink/40 mt-2">Admin only — removes every post. Use this to clear old test entries.</p>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={30} className="animate-spin text-clay" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20 text-ink/50" data-testid="wall-empty">
            <MessageCircleQuestion size={40} className="mx-auto text-clay/50" />
            <p className="mt-4">No questions yet. Be the first to ask Shabba Robo!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12" data-testid="wisdom-wall-grid">
            {items.map((it, idx) => (
              <article
                key={it.id}
                className={`fade-up bg-sand-light border rounded-3xl p-6 flex flex-col hover:-translate-y-1 transition-transform ${it.pinned ? "border-clay ring-1 ring-clay/40" : "border-border"}`}
                style={{ animationDelay: `${Math.min(idx * 0.05, 0.4)}s` }}
                data-testid={`wall-card-${it.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-clay bg-clay/10 rounded-full px-3 py-1">
                      {it.category}
                    </span>
                    {it.pinned && (
                      <span className="inline-flex items-center gap-1 text-xs font-bold text-ochre bg-ochre/10 rounded-full px-2.5 py-1" data-testid={`wall-featured-${it.id}`}>
                        <Star size={11} className="fill-ochre" /> Featured
                      </span>
                    )}
                    {isAdmin && it.hidden && (
                      <span className="inline-flex items-center gap-1 text-xs font-bold text-clay bg-clay/10 rounded-full px-2.5 py-1" data-testid={`wall-hidden-${it.id}`}>
                        <EyeOff size={11} /> Hidden
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-ink/40">@{it.handle}</span>
                    {isAdmin && it.reports > 0 && (
                      <span
                        className="inline-flex items-center gap-1 text-xs font-bold text-clay bg-clay/10 rounded-full px-2 py-0.5"
                        data-testid={`wall-reports-${it.id}`}
                        title={`Reported ${it.reports}x` + (it.report_reasons ? " — " + Object.entries(it.report_reasons).map(([k, v]) => `${k}: ${v}`).join(", ") : "")}
                      >
                        <Flag size={11} /> {it.reports}
                      </span>
                    )}
                    {isAdmin && it.hidden && (
                      <button
                        onClick={() => onToggleHide(it.id, it.hidden)}
                        className="text-ink/30 hover:text-leaf transition-colors"
                        title="Restore to public"
                        data-testid={`wall-restore-${it.id}`}
                      >
                        <RotateCcw size={15} />
                      </button>
                    )}
                    {isAdmin && (
                      <button
                        onClick={() => onTogglePin(it.id, it.pinned)}
                        className={`transition-colors ${it.pinned ? "text-ochre" : "text-ink/30 hover:text-ochre"}`}
                        title={it.pinned ? "Unpin" : "Pin to top"}
                        data-testid={`wall-pin-${it.id}`}
                      >
                        <Pin size={15} className={it.pinned ? "fill-ochre" : ""} />
                      </button>
                    )}
                    {isAdmin && (
                      <button
                        onClick={() => onDelete(it.id)}
                        className="text-ink/30 hover:text-clay transition-colors"
                        title="Delete this post"
                        data-testid={`wall-delete-${it.id}`}
                      >
                        <Trash2 size={15} />
                      </button>
                    )}
                  </div>
                </div>

                <p className="font-display font-bold text-lg text-ink mt-4 leading-snug">
                  “{it.problem}”
                </p>

                <div className="flex gap-3 mt-4 pt-4 border-t border-border">
                  <img
                    src={AVATAR_HERO}
                    alt=""
                    className="w-9 h-9 rounded-full object-cover ring-1 ring-clay/30 shrink-0"
                  />
                  <p className="text-sm text-ink/75 leading-relaxed line-clamp-6 whitespace-pre-line">
                    {it.answer}
                  </p>
                </div>

                <div className="mt-4 flex items-center justify-between">
                  <button
                    onClick={() => onLike(it.id)}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-ink/60 hover:text-clay transition-colors"
                    data-testid={`wall-like-${it.id}`}
                  >
                    <Heart
                      size={16}
                      className={liked[it.id] ? "fill-clay text-clay" : ""}
                    />
                    {it.likes} {it.likes === 1 ? "person felt this" : "people felt this"}
                  </button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        disabled={reported[it.id]}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-ink/40 hover:text-clay transition-colors disabled:opacity-50"
                        title="Report this post"
                        data-testid={`wall-report-${it.id}`}
                      >
                        <Flag size={13} /> {reported[it.id] ? "Reported" : "Report"}
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {REPORT_REASONS.map((r) => (
                        <DropdownMenuItem
                          key={r.id}
                          onClick={() => onReport(it.id, r.id)}
                          data-testid={`wall-report-${it.id}-${r.id}`}
                        >
                          {r.label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
});

WisdomWall.displayName = "WisdomWall";
